/**
 * **Quem se beneficia deste item** — a frase da tela e a marca do encaixe.
 *
 * ## O defeito que isto conserta
 *
 * As cinco ferramentas de 900 de ouro do Posto de Expedição (Lâmina Afiada, Picareta de Aço,
 * Pincel de Escavação, Lupa de Bolso, Tesoura de Poda) são **uma por guilda**, e a loja não dizia
 * qual. Comprava-se a lâmina achando que era do mercenário e ela era do botânico — o servidor só
 * recusa depois, na hora de equipar (`guilda_do_item`, `explorers.service.ts`), e aí o ouro já
 * foi. O dado sempre existiu na resposta do catálogo (`guilda`); morria no caminho para a tela.
 *
 * O mesmo texto vale na oficina, onde o problema tem uma face a mais: lá convivem coisa da
 * **squad** (equipamento e poção de integrante) e coisa do **pescador** (apetrecho e poção de
 * pesca), e "Poção de Cura" e "Poção de Vigor" são nomes que não separam as duas.
 *
 * ## Por que aqui, e não em cada tela
 *
 * Loja e oficina precisam dizer a **mesma** frase para o mesmo item — senão o jogador aprende a
 * regra numa tela e ela muda de nome na outra. É a mesma razão de `raridade.js` existir.
 *
 * ## Por que recebe o `t`
 *
 * Devolver a chave e deixar a tela traduzir espalharia `exploration.guildas.${x}` por dois
 * componentes; devolver a frase pronta exige o tradutor. O `t` entra por parâmetro em vez de
 * importar o `i18n` porque assim a função é pura e o teste não precisa montar a app.
 */

/** As categorias da oficina que produzem coisa do PESCADOR, e não da squad. */
const DO_PESCADOR = new Set(['apetrecho', 'pocao']);

/**
 * O beneficiário, ou `null` quando o item não é de exploração.
 *
 * O `null` é o que deixa esta função viver dentro do `LojaPainel`, que é o painel das **seis**
 * lojas: vara, isca, barco e casa não têm guilda nem integrante, e responder "qualquer integrante
 * da squad" para uma vara seria pior do que calar. O discriminador é o `tipo` do catálogo de
 * exploração (`equipamento` / `consumivel`) — a vara chega com `type: 'rod'`, que não é nenhum
 * dos dois.
 *
 * @param {{categoria?: string, tipo?: string, guilda?: string|null}} item
 * @returns {{quem: 'pescador'|'squad'|'guilda', guilda?: string}|null}
 */
export function beneficiarioDe(item) {
  if (!item) return null;
  if (DO_PESCADOR.has(item.categoria)) return { quem: 'pescador' };
  if (item.tipo !== 'equipamento' && item.tipo !== 'consumivel') return null;
  if (item.guilda) return { quem: 'guilda', guilda: item.guilda };
  return { quem: 'squad' };
}

/**
 * A frase inteira: "Só integrante Botânico", "Qualquer integrante da squad", "Você, na pescaria".
 *
 * @param {object} item
 * @param {(chave: string, params?: object) => string} t
 */
export function paraQuemEmTexto(item, t) {
  const b = beneficiarioDe(item);
  if (!b) return '';
  if (b.quem === 'pescador') return t('exploration.paraQuem.pescador');
  if (b.quem === 'squad') return t('exploration.paraQuem.qualquerIntegrante');
  return t('exploration.paraQuem.guilda', { guilda: t(`exploration.guildas.${b.guilda}`) });
}

/**
 * A marca do encaixe: o nome da guilda, e **vazio** para o que qualquer um usa.
 *
 * Curta de propósito — ela mora numa tarja de ~70px e o encaixe não é lugar de frase. Marcar
 * também o item livre encheria a grade inteira de tarja e a marca deixaria de marcar.
 */
export function marcaDeGuilda(item, t) {
  const b = beneficiarioDe(item);
  return b?.quem === 'guilda' ? t(`exploration.guildas.${b.guilda}`) : '';
}
